/**
 * AI use-case template vocabulary — client copy of the v4.0 contract
 * (DC-1781). The client never imports server code; these values mirror
 * server/banking/useCaseInventory/templateV4Adapter.ts and the registryParity
 * discipline applies: change one, change both.
 *
 * The vocabulary is TEMPLATE-VERSIONED content (v4 ships ten categories);
 * every surface rendering it also renders TEMPLATE_VERSION (UXT-20).
 *
 * ARC framework and template structure: Alexa Philo's pre-existing IP, used
 * with attribution. Category names and field labels only — no ARC
 * control-library content.
 */

export const TEMPLATE_VERSION = '4.0';

export const CATEGORIES = [
  'Prediction & Scoring',
  'Explainability & Interpretability',
  'Monitoring & Surveillance',
  'Data Integration & Preparation',
  'Forecasting & Simulation',
  'Document & Language Processing',
  'Conversational & Agentic Assistance',
  'Reporting & Visualization',
  'Security & Adversarial Testing',
  'Governance & Workflow Automation',
] as const;

export const LIFECYCLE_STAGES = [
  { value: 'planned', label: 'Planned' },
  { value: 'pilot', label: 'Pilot' },
  { value: 'production', label: 'Production' },
  { value: 'retired', label: 'Retired' },
] as const;

export const AUTONOMY_LEVELS = [
  { value: 'advisory_only', label: 'Advisory only (human makes all decisions)' },
  { value: 'human_in_the_loop', label: 'Human-in-the-loop (human approves each output before use)' },
  { value: 'human_on_the_loop', label: 'Human-on-the-loop (human monitors continuously, can intervene)' },
  { value: 'fully_automated', label: 'Fully automated (no routine human review)' },
] as const;

export const EVIDENCE_STATES = [
  { value: 'yes_in_hand', label: 'Yes (in hand)' },
  { value: 'no', label: 'No' },
  { value: 'partial', label: 'Partial' },
  { value: 'held_by_vendor', label: 'Held by vendor (not yet requested)' },
] as const;

export const REVERSIBILITY_LEVELS = [
  { value: 'easily_reversible', label: 'Easily reversible' },
  { value: 'reversible_with_cost', label: 'Reversible with cost or delay' },
  { value: 'not_reversible', label: 'Not reversible' },
] as const;

export const PREVIOUSLY_INVENTORIED = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
  { value: 'unsure', label: 'Unsure' },
] as const;

/**
 * The five-value record-status chip (accepted UX §6.1). Always icon + text —
 * never color alone.
 */
export const RECORD_STATUS: Record<string, { label: string; glyph: string }> = {
  draft: { label: 'Draft', glyph: '○' },
  scope_pending: { label: 'Scope pending', glyph: '◐' },
  assessed_decision_pending: { label: 'Assessed, decision pending', glyph: '◑' },
  decision_current: { label: 'Decision current', glyph: '●' },
  decision_expired: { label: 'Decision expired', glyph: '⊘' },
};

export interface UseCaseSummary {
  id: string;
  useCaseRef: string;
  name: string;
  accountableOwner: string;
  categoryPrimary: string;
  categoriesRelated: string[];
  lifecycleStage: string;
  recordStatus: string;
  source: string;
  templateVersion: string | null;
  sourceFilename: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ImportSummary {
  id: string;
  sourceFilename: string;
  templateVersion: string;
  rowCountTotal: number;
  rowCountImported: number;
  rowCountRejected: number;
  rowCountSkippedSynthetic: number;
  createdAt: string;
}

/**
 * ARC mapping-state chip labels (DC-1782). The five typed states the server
 * derives; `unmapped` and an absent entry both render "Not mapped" — absence
 * is information, never a fabricated state.
 */
export const ARC_SCOPE_STATES: Record<string, { label: string; glyph: string }> = {
  unmapped: { label: 'Not mapped', glyph: '○' },
  partially_mapped: { label: 'Mapping in review', glyph: '◐' },
  stale_version: { label: 'Mapping out of date', glyph: '⟳' },
  reviewer_unconfirmed: { label: 'Awaiting validation', glyph: '◑' },
  validated: { label: 'Scope validated', glyph: '●' },
};

/**
 * Client copy of the versioned ARC identifier subset — IDENTIFIERS ONLY
 * (control codes; no ARC titles, names, definitions, or any other
 * workbook-derived content), mirroring
 * server/banking/arcMapping/suggestionRuleset.ts; the registryParity
 * discipline applies: change one, change both. ARC framework: Alexa Philo's
 * pre-existing IP, identifiers used with attribution. This is a PROVISIONAL
 * Credit Scoring demo subset — never presented as the complete ARC v6
 * baseline; the entitled ARC catalog/compiler (Epic B) supersedes it as a
 * new ruleset version.
 */
export const ARC_MAPPING_RULESET_VERSION = 'cs-demo-subset-r1';
export const ARC_CATALOG_VERSION = 'arc_v6_2026-07-25';

/** Rendered wherever the subset is presented; never claim completeness. */
export const ARC_SUBSET_LABEL =
  'Provisional Credit Scoring demo subset (ARC v6 source) — identifiers only; not the complete ARC v6 baseline';

/** Rendered beside each identifier until the entitled ARC catalog ships names. */
export const ARC_IDENTIFIER_NOTE =
  'Identifier only — the control and test definitions live in your ARC workbook until the versioned ARC catalog arrives.';

export const ARC_BASELINE_UNIVERSE = [
  { controlCode: 'CS-1' },
  { controlCode: 'CS-2' },
  { controlCode: 'CS-3' },
  { controlCode: 'CS-4' },
  { controlCode: 'CS-5' },
] as const;

/** Evidence-leg channel labels (UX §8.2): which evidence a test needs. */
export const EVIDENCE_CHANNELS: Record<string, string> = {
  data: 'data',
  model: 'model',
  ops: 'ops',
  doc: 'doc',
  interview: 'interview',
  external: 'external',
};

// ============================================================================
// Credit Scoring demo pack (DC-1784) — synthetic seed, admin-only load.
// ============================================================================

export interface DemoPackCaseStatus {
  ref: string;
  name: string;
  categoryPrimary: string;
  present: boolean;
  suggestible: boolean;
}

export interface DemoPackStatus {
  packId: string;
  packVersion: string;
  filename: string;
  label: string;
  institution: string;
  caseCount: number;
  presentCount: number;
  loaded: boolean;
  cases: DemoPackCaseStatus[];
}

export interface DemoPackLoadResult {
  alreadyLoaded: boolean;
  importId: string | null;
  importedCount: number;
  alreadyPresentCount: number;
  unexpectedRejections: { rowNumber: number; useCaseRef: string | null }[];
  suggestionsGenerated: string[];
  suggestionsAlreadyPresent: string[];
}

/**
 * Download the generated SYNTHETIC demo-pack workbook (DC-1784) — a real v4
 * template file, so the demo can walk the same upload path a bank would.
 * Returns false when the fetch failed (caller decides how to say so).
 */
export async function downloadDemoPackWorkbook(
  authFetch: (url: string, options?: RequestInit) => Promise<Response>
): Promise<boolean> {
  const res = await authFetch('/api/use-cases/demo-pack/workbook');
  if (!res.ok) return false;
  const disposition = res.headers.get('content-disposition') ?? '';
  const filename =
    /filename="([^"]+)"/.exec(disposition)?.[1] ?? 'credit-scoring-demo-pack-synthetic.xlsx';
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
  return true;
}

/**
 * Download a committed import's persisted error report (DC-1783) — the
 * server-built CSV of per-row rejections. Durable: works from the inventory
 * banner or import history long after the upload session ended. Returns
 * false when the report could not be fetched (caller decides how to say so).
 */
export async function downloadImportErrorReport(
  authFetch: (url: string, options?: RequestInit) => Promise<Response>,
  importId: string
): Promise<boolean> {
  const res = await authFetch(`/api/use-cases/imports/${importId}/report`);
  if (!res.ok) return false;
  const disposition = res.headers.get('content-disposition') ?? '';
  const filename = /filename="([^"]+)"/.exec(disposition)?.[1] ?? 'import-error-report.csv';
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
  return true;
}
